import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Select, InputNumber, Timeline, Descriptions, Input } from 'antd';
import { PlusOutlined, PlayCircleOutlined, CheckCircleOutlined, ClockCircleOutlined, ThunderboltOutlined, WarningOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useSchedules, useAutoSchedule, useStartJob, useCompleteJob } from '@/hooks/useManufacturing';
import type { JobSchedule } from '@/services/manufacturing.service';

const JobSchedulingPage: React.FC = () => {
  const [form] = Form.useForm();
  const [autoScheduleModalVisible, setAutoScheduleModalVisible] = useState(false);
  const [completeJobId, setCompleteJobId] = useState<string | null>(null);
  const [completeModalVisible, setCompleteModalVisible] = useState(false);

  // API hooks
  const { data: schedules = [], isLoading } = useSchedules();
  const autoSchedule = useAutoSchedule();
  const startJob = useStartJob();
  const completeJob = useCompleteJob();

  // Extend schedules with additional display data
  const displayData = (schedules as any[]).map(schedule => ({
    ...schedule,
    work_order_number: schedule.work_order?.work_order_number || `WO-${schedule.work_order_id}`,
    workstation_name: schedule.workstation?.workstation_name || 'Unknown',
    shift_name: schedule.shift?.shift_name,
  }));

  const priorityColors: Record<string, string> = {
    Low: 'default',
    Medium: 'blue',
    High: 'orange',
    Urgent: 'red',
  };

  const statusColors: Record<string, string> = {
    Draft: 'default',
    Scheduled: 'blue',
    'In Progress': 'processing',
    Completed: 'success',
    Delayed: 'error',
    Cancelled: 'default',
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Schedule #',
      dataIndex: 'schedule_number',
      key: 'schedule_number',
      width: 150,
      fixed: 'left',
    },
    {
      title: 'Work Order',
      dataIndex: 'work_order_number',
      key: 'work_order_number',
      width: 130,
    },
    {
      title: 'Workstation',
      dataIndex: 'workstation_name',
      key: 'workstation_name',
      width: 180,
    },
    {
      title: 'Shift',
      dataIndex: 'shift_name',
      key: 'shift_name',
      width: 150,
      render: (shift?: string) => shift || '-',
    },
    {
      title: 'Scheduled Date',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      width: 130,
      render: (date: string) => dayjs(date).format('DD-MMM-YYYY'),
      sorter: (a, b) => dayjs(a.scheduled_date).unix() - dayjs(b.scheduled_date).unix(),
    },
    {
      title: 'Duration',
      dataIndex: 'scheduled_duration_hours',
      key: 'scheduled_duration_hours',
      width: 100,
      align: 'right',
      render: (hours: number) => `${hours} hrs`,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: string) => (
        <Tag color={priorityColors[priority]}>{priority}</Tag>
      ),
      filters: [
        { text: 'Low', value: 'Low' },
        { text: 'Medium', value: 'Medium' },
        { text: 'High', value: 'High' },
        { text: 'Urgent', value: 'Urgent' },
      ],
      onFilter: (value, record) => record.priority === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={statusColors[status]}>{status}</Tag>
      ),
      filters: [
        { text: 'Scheduled', value: 'Scheduled' },
        { text: 'In Progress', value: 'In Progress' },
        { text: 'Completed', value: 'Completed' },
        { text: 'Delayed', value: 'Delayed' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 130,
      render: (date?: string) => {
        if (!date) return '-';
        const isOverdue = dayjs(date).isBefore(dayjs());
        return (
          <span style={{ color: isOverdue ? '#f5222d' : undefined }}>
            {dayjs(date).format('DD-MMM-YYYY')}
          </span>
        );
      },
    },
    {
      title: 'Quantity',
      key: 'quantity',
      width: 120,
      render: (_: any, record: JobSchedule) => (
        <span>
          {record.produced_quantity}/{record.planned_quantity}
        </span>
      ),
    },
    {
      title: 'Flags',
      key: 'flags',
      width: 100,
      render: (_: any, record: JobSchedule) => (
        <Space>
          {record.is_auto_scheduled && <Tag color="cyan" title="Auto-scheduled">Auto</Tag>}
          {record.has_conflicts && <WarningOutlined style={{ color: '#faad14' }} title="Has conflicts" />}
          {record.is_delayed && <ClockCircleOutlined style={{ color: '#f5222d' }} title="Delayed" />}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 180,
      render: (_: any, record: any) => (
        <Space size="small">
          {record.status === 'Scheduled' && (
            <Button
              type="primary"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleStartJob(record.id)}
              loading={startJob.isPending}
            >
              Start
            </Button>
          )}
          {record.status === 'In Progress' && (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => openCompleteModal(record)}
            >
              Complete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleStartJob = (id: string) => {
    startJob.mutate(id);
  };

  const openCompleteModal = (schedule: any) => {
    setCompleteJobId(schedule.id);
    form.setFieldsValue({
      produced_quantity: schedule.planned_quantity,
      rejected_quantity: 0,
      rework_quantity: 0,
      finished_serial_or_tag: '',
      finished_item_id: '',
    });
    setCompleteModalVisible(true);
  };

  const handleCompleteJob = async () => {
    try {
      const values = await form.validateFields();
      if (completeJobId) {
        await completeJob.mutateAsync({
          id: completeJobId,
          produced_quantity: values.produced_quantity,
          rejected_quantity: values.rejected_quantity,
          rework_quantity: values.rework_quantity,
          finished_serial_or_tag: values.finished_serial_or_tag?.trim() || undefined,
          finished_item_id: values.finished_item_id?.trim() || undefined,
        });
        setCompleteModalVisible(false);
        setCompleteJobId(null);
        form.resetFields();
      }
    } catch (error) {
      console.error('Complete job failed:', error);
    }
  };

  const handleAutoSchedule = async () => {
    try {
      const values = await form.validateFields();
      
      await autoSchedule.mutateAsync(values.work_orders);
      
      setAutoScheduleModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Auto-schedule failed:', error);
    }
  };

  const getScheduleSummary = () => {
    return {
      total: displayData.length,
      scheduled: displayData.filter((s: any) => s.status === 'Scheduled').length,
      inProgress: displayData.filter((s: any) => s.status === 'In Progress').length,
      completed: displayData.filter((s: any) => s.status === 'Completed').length,
      delayed: displayData.filter((s: any) => s.is_delayed).length,
      conflicts: displayData.filter((s: any) => s.has_conflicts).length,
    };
  };

  const summary = getScheduleSummary();

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <ThunderboltOutlined />
            <span>Job Scheduling</span>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ClockCircleOutlined />} onClick={() => setAutoScheduleModalVisible(true)}>
              Auto-Schedule
            </Button>
          </Space>
        }
      >
        {/* Summary Cards */}
        <div style={{ marginBottom: 16 }}>
          <Space size="large">
            <Descriptions size="small" column={6} bordered>
              <Descriptions.Item label="Total">{summary.total}</Descriptions.Item>
              <Descriptions.Item label="Scheduled">
                <Tag color="blue">{summary.scheduled}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="In Progress">
                <Tag color="processing">{summary.inProgress}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Completed">
                <Tag color="success">{summary.completed}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Delayed">
                <Tag color="error">{summary.delayed}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Conflicts">
                <Tag color="warning">{summary.conflicts}</Tag>
              </Descriptions.Item>
            </Descriptions>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={displayData}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1600 }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} schedules`,
          }}
          locale={{
            emptyText: 'No job schedules found. Create schedules or use auto-schedule to get started.',
          }}
          rowClassName={(record: any) => {
            if (record.has_conflicts) return 'row-conflict';
            if (record.is_delayed) return 'row-delayed';
            return '';
          }}
        />
      </Card>

      {/* Auto-Schedule Modal */}
      <Modal
        title={
          <Space>
            <ClockCircleOutlined />
            <span>Auto-Schedule Work Orders</span>
          </Space>
        }
        open={autoScheduleModalVisible}
        onOk={handleAutoSchedule}
        onCancel={() => {
          setAutoScheduleModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={autoSchedule.isPending}
        width={700}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item
            name="work_orders"
            label="Select Work Orders"
            rules={[{ required: true, message: 'Please select work orders' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select work orders to schedule"
              options={[
                { label: 'WO-2025-004 - Due: 2025-12-30', value: 'wo4' },
                { label: 'WO-2025-005 - Due: 2025-12-29', value: 'wo5' },
                { label: 'WO-2025-006 - Due: 2026-01-05', value: 'wo6' },
              ]}
            />
          </Form.Item>

          <div style={{ marginTop: 20, padding: 16, backgroundColor: '#f6f9fc', borderRadius: 4 }}>
            <Timeline>
              <Timeline.Item color="blue">
                <strong>Step 1:</strong> Sort work orders by due date (earliest first)
              </Timeline.Item>
              <Timeline.Item color="blue">
                <strong>Step 2:</strong> Find available workstations with required capacity
              </Timeline.Item>
              <Timeline.Item color="blue">
                <strong>Step 3:</strong> Check material availability
              </Timeline.Item>
              <Timeline.Item color="blue">
                <strong>Step 4:</strong> Assign to earliest available slot
              </Timeline.Item>
              <Timeline.Item color="green">
                <strong>Step 5:</strong> Generate schedules and detect conflicts
              </Timeline.Item>
            </Timeline>
          </div>

          <div style={{ marginTop: 16 }}>
            <strong>Scheduling Logic:</strong>
            <ul style={{ marginTop: 8 }}>
              <li>Priority: Due date-based (earliest first)</li>
              <li>Optimization: Minimize makespan and maximize utilization</li>
              <li>Conflict Detection: Automatic</li>
              <li>Overtime: Considered if regular capacity is insufficient</li>
            </ul>
          </div>
        </Form>
      </Modal>

      {/* Complete Job Modal */}
      <Modal
        title="Complete Job"
        open={completeModalVisible}
        onOk={handleCompleteJob}
        onCancel={() => {
          setCompleteModalVisible(false);
          setCompleteJobId(null);
          form.resetFields();
        }}
        confirmLoading={completeJob.isPending}
      >
        <p style={{ marginBottom: 12, color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>
          If produced quantity is greater than zero, the system posts <strong>finished goods</strong> to the work
          order&apos;s <strong>target warehouse</strong> and records a traceability output row when the WO has a
          stockable finished item (or you supply an item ID below).
        </p>
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            name="produced_quantity"
            label="Produced Quantity"
            rules={[{ required: true, message: 'Please enter produced quantity' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="rejected_quantity"
            label="Rejected Quantity"
          >
            <InputNumber min={0} defaultValue={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="rework_quantity"
            label="Rework Quantity"
          >
            <InputNumber min={0} defaultValue={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="finished_serial_or_tag"
            label="Serial / tag (traceability)"
            extra="Optional. Stored on the traceability matrix with this job completion."
          >
            <Input placeholder="e.g. heat tag or unit serial" allowClear />
          </Form.Item>

          <Form.Item
            name="finished_item_id"
            label="Finished item ID (override)"
            extra="Leave blank to use the work order’s item (item_id or item_code)."
          >
            <Input placeholder="Inventory item UUID if WO has no item link" allowClear />
          </Form.Item>
        </Form>
      </Modal>

      <style>{`
        .row-conflict {
          background-color: #fffbe6;
        }
        .row-delayed {
          background-color: #fff1f0;
        }
      `}</style>
    </div>
  );
};

export default JobSchedulingPage;

